import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, CheckCircle2, XCircle, User, TrendingDown, TrendingUp, Minus, ChevronDown, ChevronUp, MessageSquare, Camera, Scale } from 'lucide-react';

function CoachCheckins() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [overview, setOverview] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackCheckinId, setFeedbackCheckinId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const res = await axios.get(`${API}/checkin/coach/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOverview(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/checkin/coach/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch (error) {
      console.error(error);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackCheckinId || !feedbackText.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/checkin/${feedbackCheckinId}/feedback`, 
        { feedback: feedbackText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackText('');
      setFeedbackCheckinId(null);
      if (selectedUser) fetchUserDetail(selectedUser);
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getTrend = (checkins, field) => {
    if (!checkins || checkins.length < 2) return null;
    const latest = checkins[0]?.[field];
    const prev = checkins[1]?.[field];
    if (latest == null || prev == null) return null;
    const diff = latest - prev;
    return { diff: diff.toFixed(1), direction: diff < 0 ? 'down' : diff > 0 ? 'up' : 'same' };
  };

  const renderEvolutionChart = (checkins, field, label, unit, color) => {
    if (!checkins || checkins.length === 0) return null;
    const data = checkins.filter(c => c[field] != null).reverse();
    if (data.length === 0) return null;
    
    const values = data.map(c => c[field]);
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;

    return (
      <div className="bg-gray-800 rounded-lg p-4" data-testid={`chart-${field}`}>
        <p className="text-sm text-gray-400 mb-3">{label}</p>
        <div className="flex items-end gap-1 h-24">
          {data.map((c, i) => {
            const height = ((c[field] - min) / range) * 80 + 20;
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-gray-500">{c[field]}{unit}</span>
                <div className={`w-full rounded-t ${color}`} style={{ height: `${height}%` }} />
                <span className="text-[9px] text-gray-600">S{c.week_number}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="text-gold text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-20" data-testid="coach-checkins-page">
      <div className="max-w-5xl mx-auto space-y-6">
        
        <div className="flex items-center gap-4 mb-2">
          <Button variant="ghost" onClick={() => selectedUser ? (setSelectedUser(null), setUserDetail(null)) : navigate('/mkdietal/admin')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="h-5 w-5 mr-1" />
            {selectedUser ? 'Volver a lista' : 'Volver'}
          </Button>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gold flex items-center justify-center gap-2">
            <Scale className="h-8 w-8" />
            Check-ins PRO
          </h1>
          <p className="text-gray-400 mt-1">Control semanal de usuarios PRO</p>
        </div>

        {/* Vista overview */}
        {!selectedUser && (
          <div className="space-y-4">
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Estado esta semana</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-900/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-400">{overview.filter(u => u.completed).length}</p>
                    <p className="text-xs text-gray-400">Completados</p>
                  </div>
                  <div className="bg-red-900/20 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-400">{overview.filter(u => !u.completed).length}</p>
                    <p className="text-xs text-gray-400">Pendientes</p>
                  </div>
                </div>

                {overview.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay usuarios PRO activos</p>
                ) : (
                  <div className="space-y-2">
                    {overview.map((u) => (
                      <div key={u.user_id}
                        onClick={() => fetchUserDetail(u.user_id)}
                        className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors"
                        data-testid={`user-row-${u.user_id}`}>
                        {u.foto_perfil ? (
                          <img src={`${API.replace('/api','')}${u.foto_perfil}`} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-white font-medium">{u.nombre}</p>
                          {u.completed && u.checkin && (
                            <p className="text-xs text-gray-400">Peso: {u.checkin.weight} kg</p>
                          )}
                        </div>
                        {u.completed ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <XCircle className="w-6 h-6 text-red-500" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Vista detalle usuario */}
        {selectedUser && userDetail && (
          <div className="space-y-4">
            {/* Info usuario */}
            <Card className="card-mk border-gray-800">
              <CardContent className="p-4 flex items-center gap-4">
                {userDetail.user?.foto_perfil ? (
                  <img src={`${API.replace('/api','')}${userDetail.user.foto_perfil}`} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <p className="text-white text-xl font-bold">{userDetail.user?.nombre}</p>
                  <p className="text-gray-400 text-sm">{userDetail.checkins?.length || 0} check-ins registrados</p>
                </div>
              </CardContent>
            </Card>

            {/* Graficas de evolucion */}
            {userDetail.checkins?.length > 0 && (
              <Card className="card-mk border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-gold" />
                    Evolucion
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {renderEvolutionChart(userDetail.checkins, 'weight', 'Peso (kg)', 'kg', 'bg-blue-500')}
                  {renderEvolutionChart(userDetail.checkins, 'body_fat', '% Grasa corporal', '%', 'bg-orange-500')}
                  {renderEvolutionChart(userDetail.checkins, 'waist', 'Cintura (cm)', 'cm', 'bg-purple-500')}
                  
                  {/* Tendencia actual */}
                  {userDetail.checkins.length >= 2 && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {[
                        { field: 'weight', label: 'Peso', unit: 'kg' },
                        { field: 'body_fat', label: 'Grasa', unit: '%' },
                        { field: 'waist', label: 'Cintura', unit: 'cm' }
                      ].map(({ field, label, unit }) => {
                        const trend = getTrend(userDetail.checkins, field);
                        if (!trend) return null;
                        return (
                          <div key={field} className="bg-gray-800 rounded-lg p-2 text-center">
                            <p className="text-[10px] text-gray-500">{label}</p>
                            <div className="flex items-center justify-center gap-1">
                              {trend.direction === 'down' ? (
                                <TrendingDown className="w-4 h-4 text-green-400" />
                              ) : trend.direction === 'up' ? (
                                <TrendingUp className="w-4 h-4 text-red-400" />
                              ) : (
                                <Minus className="w-4 h-4 text-gray-400" />
                              )}
                              <span className={`text-sm font-bold ${
                                trend.direction === 'down' ? 'text-green-400' : trend.direction === 'up' ? 'text-red-400' : 'text-gray-400'
                              }`}>
                                {trend.diff > 0 ? '+' : ''}{trend.diff}{unit}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Historial de check-ins */}
            <Card className="card-mk border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-lg">Historial de Check-ins</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(!userDetail.checkins || userDetail.checkins.length === 0) ? (
                  <p className="text-gray-500 text-center py-4">Sin check-ins registrados</p>
                ) : (
                  userDetail.checkins.map((checkin) => (
                    <div key={checkin.id} className="bg-gray-800 rounded-lg p-4 space-y-3" data-testid={`checkin-${checkin.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">Semana {checkin.week_number} - {checkin.year}</p>
                          <p className="text-xs text-gray-400">{new Date(checkin.date).toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-700 rounded p-2">
                          <p className="text-xs text-gray-400">Peso</p>
                          <p className="text-white font-bold">{checkin.weight} kg</p>
                        </div>
                        {checkin.body_fat && (
                          <div className="bg-gray-700 rounded p-2">
                            <p className="text-xs text-gray-400">Grasa</p>
                            <p className="text-white font-bold">{checkin.body_fat}%</p>
                          </div>
                        )}
                        {checkin.waist && (
                          <div className="bg-gray-700 rounded p-2">
                            <p className="text-xs text-gray-400">Cintura</p>
                            <p className="text-white font-bold">{checkin.waist} cm</p>
                          </div>
                        )}
                      </div>

                      {/* Fotos comparativas */}
                      {(checkin.photo_front || checkin.photo_side || checkin.photo_back) && (
                        <div className="space-y-2">
                          <p className="text-xs text-gray-400 flex items-center gap-1"><Camera className="w-3 h-3" /> Fotos de progreso</p>
                          <div className="grid grid-cols-3 gap-2">
                            {['photo_front', 'photo_side', 'photo_back'].map((key) => (
                              checkin[key] && (
                                <img key={key} src={`${API.replace('/api','')}${checkin[key]}`} alt={key}
                                  className="w-full aspect-[3/4] object-cover rounded-lg border border-gray-700" />
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {checkin.notes && (
                        <p className="text-sm text-gray-300 bg-gray-700 rounded p-2 italic">"{checkin.notes}"</p>
                      )}

                      {/* Feedback del coach */}
                      {checkin.coach_feedback && (
                        <div className="bg-gold/10 border border-gold/30 rounded-lg p-3">
                          <p className="text-xs text-gold mb-1 flex items-center gap-1"><MessageSquare className="w-3 h-3" /> Tu feedback</p>
                          <p className="text-sm text-gray-200">{checkin.coach_feedback}</p>
                          <p className="text-[10px] text-gray-500 mt-1">{checkin.feedback_date && new Date(checkin.feedback_date).toLocaleDateString('es-ES')}</p>
                        </div>
                      )}

                      {/* Añadir feedback */}
                      {feedbackCheckinId === checkin.id ? (
                        <div className="space-y-2">
                          <Textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)}
                            placeholder="Escribe tu feedback para este check-in..."
                            className="bg-gray-700 border-gray-600 text-white min-h-[60px]" />
                          <div className="flex gap-2">
                            <Button onClick={submitFeedback} disabled={saving} className="bg-gold text-black hover:bg-gold/80 flex-1" data-testid="save-feedback-btn">
                              {saving ? 'Guardando...' : 'Guardar'}
                            </Button>
                            <Button variant="outline" onClick={() => { setFeedbackCheckinId(null); setFeedbackText(''); }}
                              className="border-gray-600 text-gray-300">
                              Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm"
                          onClick={() => { setFeedbackCheckinId(checkin.id); setFeedbackText(checkin.coach_feedback || ''); }}
                          className="text-gold hover:text-gold/80 text-xs" data-testid={`feedback-btn-${checkin.id}`}>
                          <MessageSquare className="w-3 h-3 mr-1" />
                          {checkin.coach_feedback ? 'Editar feedback' : 'Añadir feedback'}
                        </Button>
                      )}
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
}

export default CoachCheckins;
